// export/ifc/psets/dotPayItemPset.ts
import { DOTPayItem } from "../../../../domain/contracts/DOTPayItem";
import type { NextId } from "../core/step";
import {
  ifcBoolean,
  ifcInteger,
  ifcReal,
  ifcText,
  writePropertySetForElement,
  type IfcProp,
} from "../core/step";

/**
 * Defensive helpers:
 * Never call string/number/boolean methods directly on domain values, because
 * at runtime they may be numbers/booleans/undefined depending on ECSQL row shapes.
 */
function asString(v: unknown): string {
  if (v === null || v === undefined) return "";
  return String(v);
}

function asNumber(v: unknown): number | undefined {
  if (v === null || v === undefined) return undefined;
  const n = typeof v === "number" ? v : Number(String(v));
  return Number.isFinite(n) ? n : undefined;
}

function asBoolean(v: unknown): boolean | undefined {
  if (v === null || v === undefined) return undefined;
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;

  const s = String(v).trim().toLowerCase();
  if (s === "true" || s === "t" || s === "1" || s === "yes" || s === "y") return true;
  if (s === "false" || s === "f" || s === "0" || s === "no" || s === "n") return false;

  return undefined;
}

function addText(props: IfcProp[], name: string, v: unknown): void {
  // Current export policy emits empty/default values to keep a stable property schema
  // per slot; review data-fidelity tradeoffs before external delivery if consumers
  // differentiate "missing" vs "zero/false/empty".
  props.push({ name, value: ifcText(asString(v)) });
}

function addInt(props: IfcProp[], name: string, v: unknown): void {
  const n = asNumber(v);
  props.push({ name, value: ifcInteger(Math.trunc(n ?? 0)) });
}

function addReal(props: IfcProp[], name: string, v: unknown): void {
  const n = asNumber(v);
  props.push({ name, value: ifcReal(n ?? 0) });
}

function addBool(props: IfcProp[], name: string, v: unknown): void {
  const b = asBoolean(v);
  props.push({ name, value: ifcBoolean(b ?? false) });
}

function toSlotNumber(v: unknown): number | undefined {
  const n = asNumber(v);
  if (!Number.isInteger(n)) return undefined;
  if ((n as number) < 1 || (n as number) > 5) return undefined;
  return n as number;
}

function buildDotPayItemProps(item: DOTPayItem, slot: number): IfcProp[] {
  const props: IfcProp[] = [];

  // Slot metadata
  addInt(props, "Slot", slot);

  // Root (slot metadata)
  addText(props, "SPECBOOK_FILTER", item.SPECBOOK_FILTER);
  addText(props, "REFITEM_SEARCH", item.REFITEM_SEARCH);

  // PayItem
  const p = item.PayItem as any;
  addText(props, "REFITEM_NM", p?.REFITEM_NM);
  addInt(props, "SpecBook", p?.SpecBook);
  addInt(props, "Class", p?.Class);
  addText(props, "Type", p?.Type);
  addInt(props, "Item", p?.Item);
  addText(props, "Description", p?.Description);
  addText(props, "Unit", p?.Unit);
  addText(props, "UnitPlan", p?.UnitPlan);
  addBool(props, "IsPlanQty", p?.IsPlanQty);

  // Quantity
  const q = item.Quantity as any;
  addText(props, "Qty.Method", q?.Method);
  addText(props, "Qty.Description", q?.Description);
  addInt(props, "Qty.FactorItem", q?.FactorItem);
  addReal(props, "Qty.FactorUser", q?.FactorUser);
  addReal(props, "Qty.Factor", q?.Factor);
  addReal(props, "Qty.Override", q?.Override);
  addReal(props, "Qty.CompQuantity", q?.CompQuantity);
  addBool(props, "Qty.RoundingConservative", q?.RoundingConservative);
  addReal(props, "Qty.RoundingIncrement", q?.RoundingIncrement);
  addInt(props, "Qty.RoundingPrecision", q?.RoundingPrecision);
  addReal(props, "Qty.ItemQuantity", q?.ItemQuantity);

  // Funding
  const f = item.Funding as any;
  addInt(props, "Funding.Code", f?.Code);
  addText(props, "Funding.Name", f?.Name);
  addText(props, "Funding.Payer", f?.Payer);
  addReal(props, "Funding.Percentage", f?.Percentage);
  addBool(props, "Funding.FederalParticipation", f?.FederalParticipation);

  // Cost
  const c = item.Cost as any;
  addText(props, "Cost.EstimateType", c?.EstimateType);
  addReal(props, "Cost.EngEstPrice", c?.EngEstPrice);
  addReal(props, "Cost.Override", c?.Override);
  addReal(props, "Cost.Factor", c?.Factor);
  addReal(props, "Cost.UnitPrice", c?.UnitPrice);
  addReal(props, "Cost.ItemCost", c?.ItemCost);

  // Time
  const t = item.Time as any;
  addReal(props, "Time.EngEstUnitPerDay", t?.EngEstUnitPerDay);
  addReal(props, "Time.UnitOverride", t?.UnitOverride);
  addReal(props, "Time.UnitFactor", t?.UnitFactor);
  addReal(props, "Time.WorkforceFactor", t?.WorkforceFactor);
  addReal(props, "Time.UnitPerDay", t?.UnitPerDay);
  addReal(props, "Time.ItemTime", t?.ItemTime);

  // ConstructionPhase
  const cp = item.ConstructionPhase as any;
  addInt(props, "ConstructionPhase.YR", cp?.YR);
  addInt(props, "ConstructionPhase.Stage", cp?.Stage);
  addInt(props, "ConstructionPhase.Class", cp?.Class);
  addText(props, "ConstructionPhase.Group", cp?.Group);
  addInt(props, "ConstructionPhase.Sequence", cp?.Sequence);
  addText(props, "ConstructionPhase.ActivityName", cp?.ActivityName);

  // PlanPrep
  const pp = item.PlanPrep as any;
  addText(props, "PlanPrep.LabelPlanP", pp?.LabelPlanP);
  addText(props, "PlanPrep.LabelNote", pp?.LabelNote);
  addText(props, "PlanPrep.Label1", pp?.Label1);
  addText(props, "PlanPrep.Label2", pp?.Label2);

  // Tabulation
  const tab = item.Tabulation as any;
  addInt(props, "Tabulation.Class", tab?.Class);
  addText(props, "Tabulation.Category", tab?.Category);
  addText(props, "Tabulation.Group", tab?.Group);
  addText(props, "Tabulation.Code", tab?.Code);
  addText(props, "Tabulation.ID", tab?.ID);
  addText(props, "Tabulation.Title", tab?.Title);
  addInt(props, "Tabulation.SheetNum", tab?.SheetNum);

  // Asset
  const a = item.Asset as any;
  addInt(props, "Asset.Project_ID", a?.Project_ID);
  addText(props, "Asset.Project", a?.Project);
  addText(props, "Asset.Index", a?.Index);
  addText(props, "Asset.Status", a?.Status);
  addInt(props, "Asset.Instance", a?.Instance);
  addInt(props, "Asset.PlanID", a?.PlanID);
  addText(props, "Asset.PlanDecoder", a?.PlanDecoder);
  addBool(props, "Asset.IsTracked", a?.IsTracked);
  addText(props, "Asset.AssetID", a?.AssetID);

  // QAQC
  const qaqc = item.QAQC as any;
  addReal(props, "QAQC.MajorCostValue", qaqc?.MajorCostValue);
  addReal(props, "QAQC.MajorTimeValue", qaqc?.MajorTimeValue);
  addBool(props, "QAQC.IsMajorItemLookup", qaqc?.IsMajorItemLookup);
  addBool(props, "QAQC.IsMajorCost", qaqc?.IsMajorCost);
  addBool(props, "QAQC.IsMajorTime", qaqc?.IsMajorTime);
  addBool(props, "QAQC.IsCheckCost", qaqc?.IsCheckCost);
  addBool(props, "QAQC.IsSiblingRequired", qaqc?.IsSiblingRequired);
  addInt(props, "QAQC.PastContractCount", qaqc?.PastContractCount);
  addBool(props, "QAQC.IsCommonItem", qaqc?.IsCommonItem);
  addText(props, "QAQC.ElementDescription", qaqc?.ElementDescription);

  // Help
  const h = item.Help as any;
  addText(props, "Help.Tip", h?.Tip);
  addText(props, "Help.Link", h?.Link);

  // Remarks
  const r = item.Remarks as any;
  addText(props, "Remarks", r?.Remarks);

  return props;
}

export function writeDotPayItemPsets(args: {
  getNextId: NextId;
  ownerHistoryId: number;
  elementId: number;
  dotPayItem1?: DOTPayItem;
  dotPayItem2?: DOTPayItem;
  dotPayItems?: DOTPayItem[];
}): string {
  const {
    getNextId,
    ownerHistoryId,
    elementId,
    dotPayItem1,
    dotPayItem2,
    dotPayItems,
  } = args;

  let out = "";

  const slots = new Map<number, DOTPayItem>();

  if (dotPayItems && dotPayItems.length > 0) {
    dotPayItems.forEach((item) => {
      const idx = toSlotNumber(item?.Index);
      if (idx !== undefined) {
        slots.set(idx, item);
      }
    });
  }

  // Backward compatibility: if dotPayItems wasn't populated, use legacy slot args
  // but only when they carry an explicit valid Index (indicating the slot exists).
  if (slots.size === 0) {
    const idx1 = toSlotNumber(dotPayItem1?.Index);
    const idx2 = toSlotNumber(dotPayItem2?.Index);
    if (dotPayItem1 && idx1 !== undefined && !slots.has(idx1)) {
      slots.set(idx1, dotPayItem1);
    }
    if (dotPayItem2 && idx2 !== undefined && !slots.has(idx2)) {
      slots.set(idx2, dotPayItem2);
    }
  }

  const ordered = Array.from(slots.entries()).sort(([a], [b]) => a - b);
  for (const [slot, item] of ordered) {
    out += writePropertySetForElement({
      getNextId,
      ownerHistoryId,
      elementId,
      psetName: `DOT Pay Item ${slot}`,
      props: buildDotPayItemProps(item, slot),
    });
  }

  return out;
}
